import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { prChipStyle, prStateSummary } from './prPresentation';

/**
 * Determine the overall status color for the PR badge based on open PRs only:
 * - default (gray) if no open PRs (all merged/closed)
 * - red if any open PR has failing CI or changes requested
 * - green if all open PRs are approved
 * - blue otherwise
 */
function badgeColor(pullRequests) {
  const open = pullRequests.filter(pr => pr.state === 'open');
  if (!open.length) return 'default';

  const hasActionable = open.some(
    pr => pr.checksState === 'failure' || pr.reviewState === 'changes_requested'
  );
  if (hasActionable) return 'error';

  const allApproved = open.every(pr => pr.reviewState === 'approved');
  if (allApproved) return 'success';

  return 'info';
}

/**
 * Collapsible section showing all pull requests for a bet, aggregated from
 * its scopes. Surfaces PRs that need attention (failing CI, changes requested)
 * at the top so the team can act without tracking Slack messages. PRs needing
 * the current user's review are shown first in amber. Merged/closed PRs appear
 * at the end to provide links to completed work.
 */
export default function BetOpenPRs({ pullRequests = [], onSelectScope }) {
  const [expanded, setExpanded] = useState(false);

  if (!pullRequests.length) return null;

  // Sort PRs with needsYourReview to the top, then by priority
  const sortedPRs = [...pullRequests].sort((a, b) => {
    // needsYourReview PRs always come first
    if (a.needsYourReview && !b.needsYourReview) return -1;
    if (!a.needsYourReview && b.needsYourReview) return 1;

    // Then sort by priority (existing logic)
    const priority = (p) => {
      if (p.state === 'merged') return 10;
      if (p.state === 'closed') return 11;
      if (p.checksState === 'failure') return 0;
      if (p.reviewState === 'changes_requested') return 1;
      if (p.reviewState === 'approved') return 2;
      if (p.reviewState === 'pending') return 3;
      if (p.isDraft) return 5;
      return 4;
    };
    return priority(a) - priority(b);
  });

  const color = badgeColor(pullRequests);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        backgroundColor: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 'unset',
          px: 0,
          '& .MuiAccordionSummary-content': { my: 0.5 },
        }}
      >
        <Chip
          size="small"
          color={color}
          variant="outlined"
          icon={<CallSplitIcon sx={{ fontSize: 14 }} />}
          label={`Pull Requests (${pullRequests.length})`}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
        <Stack spacing={0.75}>
          {sortedPRs.map((pr) => {
            const { color: chipColor, label } = prChipStyle(pr);
            const scopeLabel = pr.scopeJiraKey || pr.scopeCode;
            return (
              <Box
                key={`${pr.repoFullName}#${pr.number}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.75,
                }}
              >
                <Tooltip title={prStateSummary(pr)}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={chipColor}
                    icon={<CallSplitIcon sx={{ fontSize: 13 }} />}
                    label={`#${pr.number} ${label}`}
                    component="a"
                    href={pr.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    onClick={(e) => e.stopPropagation()}
                    sx={{ flexShrink: 0 }}
                  />
                </Tooltip>
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pr.title}
                </Typography>
                {pr.isEpicLevel ? (
                  <Chip
                    size="small"
                    variant="filled"
                    color="secondary"
                    label={pr.epicKey || 'Epic'}
                    sx={{
                      flexShrink: 0,
                      maxWidth: 120,
                    }}
                  />
                ) : scopeLabel && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={scopeLabel}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectScope?.(pr.scopeId);
                    }}
                    sx={{
                      cursor: 'pointer',
                      flexShrink: 0,
                      maxWidth: 120,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
